// Q9 S6 / D8 — Inline per-field error display.
//
// Spec (docs/specs/visit-report-per-section-validation.md §Server error
// → UI surfacing): "Anchor red error text directly under the field.
// Persists until the field changes."
//
// FormField + SelectField already render error text natively when given
// an `error` prop. This component covers the remaining surfaces:
// raw <textarea> in NotesSection, the per-row drug_name input in
// MedicationSection, and the SwitchToggle pairs in WellbeingSection's
// symptom_detail block. One error message OR an array of messages
// (cross-field rules can attach multiple errors to the same field).

import type { JSX } from "react";

interface Props {
  /** Single message OR list of messages — both render. Falsy → render nothing. */
  error?:    string | readonly string[];
  /** Optional id so the offending input can `aria-describedby={id}`. */
  id?:       string;
  className?: string;
}

export function FieldError({ error, id, className }: Props): JSX.Element | null {
  if (!error) return null;
  const messages = Array.isArray(error) ? error : [error];
  if (messages.length === 0) return null;

  return (
    <div
      id={id}
      role="alert"
      data-testid="field-error"
      className={`mt-1 text-xs text-red-600 ${className ?? ""}`}
    >
      {messages.map((msg, i) => (
        <p key={i} className="leading-snug">{msg}</p>
      ))}
    </div>
  );
}

/** Tailwind className for a raw input/textarea that has an error.
 *  FormField + SelectField already do this internally; use this helper
 *  for the manually-rendered inputs (NotesSection textarea, etc). */
export function inputErrorClassName(hasError: boolean): string {
  return hasError
    ? "border-red-400 focus:ring-red-400 bg-red-50"
    : "border-slate-300 focus:ring-navy-500";
}
